import { useEffect, useMemo, useState } from "react";
import type { CartItem } from "../context/CartContext";
import { useOrderDetail } from "../api/queries";
import {
  addCustomerDisplayCartDiscountListener,
  getCustomerDisplayCartDiscount,
} from "../lib/customer-display-cart-discount";
import {
  addCustomerDisplayOrderListener,
  getCustomerDisplayOrder,
} from "../lib/customer-display-order";
import {
  addCustomerDisplayTempCartListener,
  getCustomerDisplayTempCart,
} from "../lib/customer-display-temp-cart";
import { useCustomerCart } from "./useCustomerCart";

type OrderItemLike = {
  id: string;
  productName: string;
  quantity: number;
  lineSubtotal: string | number;
  taxTotal?: string | number;
  variantOptions?: Array<{ optionName: string; optionValue: string }>;
  modifiers?: Array<{
    label: string;
    optionValue?: string | null;
    priceDelta: number;
    optionLabel?: string | null;
  }>;
  note?: string | null;
  voidedAt?: string | Date | null;
};

function mapOrderItemToCartItem(item: OrderItemLike): CartItem {
  const lineTotal =
    Number(item.lineSubtotal) + Number(item.taxTotal ?? 0);
  const qty = Number(item.quantity) || 1;
  const variantName =
    item.variantOptions && item.variantOptions.length > 0
      ? item.variantOptions
          .map((o) => `${o.optionName}: ${o.optionValue}`)
          .join(", ")
      : "";
  const modifiers = (item.modifiers ?? []).map((m) => ({
    modifierId: `m-${item.id}-${m.label}`,
    modifierName: m.label,
    optionValue: m.optionValue ?? "",
    optionLabel: m.optionLabel ?? "",
    priceDelta: Number(m.priceDelta) || 0,
  }));
  return {
    id: item.id,
    productId: "",
    productName: item.productName,
    variantId: "",
    variantName,
    price: lineTotal / qty,
    quantity: qty,
    modifiers,
    selectedOptions: {},
    note: item.note ?? undefined,
    totalPrice: lineTotal,
  };
}

export type DisplayDiscount = {
  id: string;
  label: string;
  amount: number;
};

/**
 * รายการที่แสดงบน Customer Display:
 * - ถ้ามีการเซ็ต "order ที่จะจ่าย" (สั่งก่อนจ่ายทีหลัง) → ดึงรายการของ order นั้น (ไม่รวมรายการที่ void)
 * - ไม่มี → ใช้ cart ปัจจุบันจาก localStorage
 */
export function useCustomerDisplayItems(): {
  items: CartItem[];
  total: number;
  itemCount: number;
  source: "order" | "cart";
  discountTotal: number;
  discounts: DisplayDiscount[];
} {
  const cart = useCustomerCart();
  const [displayOrder, setDisplayOrder] = useState(() =>
    getCustomerDisplayOrder(),
  );
  const [cartDiscount, setCartDiscount] = useState(() =>
    getCustomerDisplayCartDiscount(),
  );
  const [tempCart, setTempCart] = useState(() => getCustomerDisplayTempCart());

  useEffect(() => {
    const sync = () => {
      setDisplayOrder(getCustomerDisplayOrder());
      setCartDiscount(getCustomerDisplayCartDiscount());
      setTempCart(getCustomerDisplayTempCart());
    };
    const removeOrder = addCustomerDisplayOrderListener(sync);
    const removeDiscount = addCustomerDisplayCartDiscountListener(sync);
    const removeTempCart = addCustomerDisplayTempCartListener(sync);
    const interval = setInterval(sync, 500);
    return () => {
      removeOrder();
      removeDiscount();
      removeTempCart();
      clearInterval(interval);
    };
  }, []);

  const orderDetail = useOrderDetail(
    displayOrder?.snapshot ? null : (displayOrder?.orderId ?? null),
    displayOrder?.snapshot ? null : (displayOrder?.branchId ?? null),
  );

  const { items, total, itemCount, discountTotal, discounts } = useMemo(() => {
    const buildOrderDisplay = (input: {
      items: CartItem[];
      orderGrandTotal: number;
      orderDiscountTotal: number;
      orderDiscounts: DisplayDiscount[];
    }) => {
      const paymentDiscountTotal = cartDiscount?.discountTotal ?? 0;
      const paymentDiscounts: DisplayDiscount[] = (
        cartDiscount?.discounts ?? []
      ).map((d, i) => ({
        id: `payment-discount-${i}`,
        label: d.label,
        amount: d.amount,
      }));
      return {
        items: input.items,
        total: Math.max(0, input.orderGrandTotal - paymentDiscountTotal),
        itemCount: input.items.reduce((s, i) => s + i.quantity, 0),
        source: "order" as const,
        discountTotal: input.orderDiscountTotal + paymentDiscountTotal,
        discounts: [...input.orderDiscounts, ...paymentDiscounts],
      };
    };

    if (displayOrder?.snapshot?.items) {
      const snapshot = displayOrder.snapshot;
      return buildOrderDisplay({
        items: snapshot.items,
        orderGrandTotal: snapshot.grandTotal,
        orderDiscountTotal: snapshot.discountTotal ?? 0,
        orderDiscounts: [],
      });
    }

    if (displayOrder) {
      if (!orderDetail.data) {
        return {
          items: [] as CartItem[],
          total: 0,
          itemCount: 0,
          source: "order" as const,
          discountTotal: 0,
          discounts: [] as DisplayDiscount[],
        };
      }

      const data = orderDetail.data as {
        order: { grandTotal: string | number; discountTotal?: string | number };
        items: OrderItemLike[];
        discounts?: Array<{ id: string; label: string; amount: string | number }>;
      };
      const order = data.order;
      const allItems = Array.isArray(data.items) ? data.items : [];
      const activeItems = allItems.filter(
        (i) =>
          !(i as OrderItemLike & { voidedAt?: string | Date | null }).voidedAt,
      );
      const items = activeItems.map(mapOrderItemToCartItem);
      const orderDiscounts: DisplayDiscount[] = (data.discounts ?? []).map(
        (d) => ({
          id: d.id,
          label: d.label,
          amount: Math.abs(Number(d.amount)),
        }),
      );

      return buildOrderDisplay({
        items,
        orderGrandTotal: Number(order.grandTotal) ?? 0,
        orderDiscountTotal: Number(order.discountTotal) || 0,
        orderDiscounts,
      });
    }

    // รายการใน Add Items Dialog (ເພີ່ມລາຍການ) — แสดงบน Customer Display ก่อน
    const tempCartItems = tempCart ?? [];
    if (tempCartItems.length > 0) {
      const totalTemp = tempCartItems.reduce((s, i) => s + i.totalPrice, 0);
      return {
        items: tempCartItems,
        total: totalTemp,
        itemCount: tempCartItems.reduce((s, i) => s + i.quantity, 0),
        source: "cart" as const,
        discountTotal: 0,
        discounts: [] as DisplayDiscount[],
      };
    }

    const cartTotal = cart.total;
    const discount = cartDiscount;
    const discountTotalCart = discount?.discountTotal ?? 0;
    const discountsCart: DisplayDiscount[] = (discount?.discounts ?? []).map(
      (d, i) => ({ id: `cart-discount-${i}`, label: d.label, amount: d.amount }),
    );
    return {
      items: cart.items,
      total: Math.max(0, cartTotal - discountTotalCart),
      itemCount: cart.items.reduce((s, i) => s + i.quantity, 0),
      source: "cart" as const,
      discountTotal: discountTotalCart,
      discounts: discountsCart,
    };
  }, [
    displayOrder,
    orderDetail.data,
    cart.items,
    cart.total,
    cartDiscount,
    tempCart,
  ]);

  return {
    items,
    total,
    itemCount,
    source:
      displayOrder &&
      (displayOrder.snapshot?.items || orderDetail.data)
        ? "order"
        : "cart",
    discountTotal,
    discounts,
  };
}
